import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import type { ObjectSchema } from 'yup';

import BayModal from '../../BayModal';
import {
	regexIsValidPathTest,
	regexHasForbiddenCharTest,
	regexIsMediaTest,
} from '../../utilities/regex';

const regexIsValidPath = new RegExp(regexIsValidPathTest);
const regexHasForbiddenChar = new RegExp(regexHasForbiddenCharTest);
const regexIsMedia = new RegExp(regexIsMediaTest);

interface SelectedFile {
	name: string;
	[key: string]: unknown;
}

interface EditEntryModalProps {
	t: (key: string) => string;
	title: string;
	showModal: boolean;
	onCloseFunc: () => void;
	buttonText: string;
	onSubmitFunc: (data: { name: string; data: SelectedFile | null }) => void;
	editModalType: 'file' | 'folder';
	selectedFile: SelectedFile | null;
	formSchema?: ObjectSchema<unknown>;
	formChildren?: React.ReactNode;
	initialValues?: Record<string, unknown>;
}

const EditEntryModal: React.FC<EditEntryModalProps> = ({
	t,
	title,
	showModal,
	onCloseFunc,
	buttonText,
	onSubmitFunc,
	editModalType,
	selectedFile,
}) => {
	const [nameInput, setNameInput] = useState(selectedFile?.name ?? '');
	const [inputError, setInputError] = useState(false);

	const modalType =
		editModalType === 'folder' ? t('folderName') : t('fileName');

	const handleOnSubmit = (e: React.MouseEvent | React.FormEvent) => {
		if ('keyCode' in e && (e.keyCode === 13 || ('key' in e && e.key === 'Enter'))) {
			e.stopPropagation();
			e.preventDefault();
			return;
		}

		onSubmitFunc({ name: nameInput, data: selectedFile });
	};

	const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		/**
		 * Must not be empty or blank.
		 * Must not be more than 64 characters.
		 * Must not contain forbidden chars or invalid spaces or dots
		 * Must have a valid image extension or video extension
		 */
		const target = e.target as HTMLInputElement;

		if (
			target.value.length > 0 &&
			target.value.length <= 64 &&
			regexIsValidPath.test(target.value) &&
			!regexHasForbiddenChar.test(target.value) &&
			(editModalType === 'file' ? regexIsMedia.test(target.value) : true)
		) {
			setInputError(false);
		} else {
			setInputError(true);
		}

		setNameInput(target.value);
	};

	const handleIsEnterKey = (event: React.KeyboardEvent) => {
		if (event.keyCode === 13 || event.key === 'Enter') {
			event.stopPropagation();
			event.preventDefault();
			return;
		}
	};

	return (
		<div>
			<BayModal
				backdrop="static"
				buttonText={t('save')}
				centered={true}
				closeButton={true}
				footer={
					<>
						<div>
							<Button
								className="cancel-button"
								id="create_new_entry-cancel_btn"
								variant="outline-secondary"
								onClick={() => onCloseFunc()}
							>
								{t('cancel')}
							</Button>
						</div>
						<div className="text-right">
							<Button
								className="ms-3"
								disabled={inputError || selectedFile?.name === nameInput}
								id="create_new_entry-submit_btn"
								onClick={(e) => handleOnSubmit(e)}
							>
								{buttonText}
							</Button>
						</div>
					</>
				}
				height="100%"
				show={showModal}
				title={title}
				onHideFunc={() => onCloseFunc()}
			>
				<Form id="edit_entry_object" onSubmit={(e) => handleOnSubmit(e)}>
					<Form.Group className="mb-3">
						<Form.Label className="required">{modalType}</Form.Label>
						<Form.Control
							required
							autoComplete="off"
							className={!inputError ? '' : 'is-invalid'}
							id="edit_entry_object-input_name"
							isValid={
								nameInput.length > 0 &&
								nameInput.length <= 64 &&
								regexIsValidPath.test(nameInput) &&
								(editModalType === 'file'
									? regexIsMedia.test(nameInput)
									: true) &&
								!regexHasForbiddenChar.test(nameInput)
							}
							maxLength={64}
							placeholder={`${t('enter')} ${modalType}`}
							type="text"
							value={nameInput}
							onChange={handleInputChanged}
							onKeyDown={(e) => handleIsEnterKey(e)}
						/>
						{inputError && (
							<div className="alert alert-danger mt-2" role="alert">
								<h2>
									{t(
										editModalType === 'file'
											? 'invalidFileName'
											: 'invalidFolderName',
									)}
								</h2>
								<p>
									{t(
										editModalType === 'file'
											? 'invalidFileNameContent'
											: 'invalidFolderNameContent',
									)}
								</p>
								<ul>
									<li>Must have a minimum of 1 character.</li>
									<li>{'Must not contain + = | * ? / \\ : < > $'}</li>
									<li>Must not start or end with .</li>
									<li>Must not start or end with a space</li>
									<li>Must not contain double spaces</li>
									{editModalType === 'file' && (
										<li>
											Must have a valid image extension (ie: .jpg, .png) or
											video extension (ie: .mp4, .mov)
										</li>
									)}
								</ul>
							</div>
						)}
					</Form.Group>
				</Form>
			</BayModal>
		</div>
	);
};

export default EditEntryModal;
